import { SUPPORTED_COUNTRIES } from "../../constants";
import {
	attachSubmitEvent,
	BasisTheoryElement,
	BasisTheoryTokenType,
	initBasisTheoryField,
	preValidateFields,
	tokenizeBankAccount
} from "../../components/payments/util";
import { PaymentMethod } from "./paymentsUtil";
import { FIELD_NAMES } from "../../util";
import { safeTry } from "../../services/services";

export class BankAccount extends PaymentMethod {
	constructor(bt, configuration, customTheme, api, country) {
		const { accountNumber, routingNumber, transitNumber, institutionNumber, ...billingAddressConfigs } =
			configuration.fields;

		super(billingAddressConfigs, customTheme, configuration?.onChangeValidation);
		this.configuration = configuration;
		this.api = api;
		this.bt = bt;
		this.theme = customTheme;
		this.country = country;

		attachSubmitEvent(configuration.submitButton, this.submitCallback);
	}

	init = async () => {
		const { accountNumber, routingNumber, transitNumber, institutionNumber, ...billingAddressConfigs } =
			this.configuration.fields;
		this.initializeBillingInformation(billingAddressConfigs, this.theme, this.onChange);

		this.accountNumberField = await initBasisTheoryField(
			this.bt,
			BasisTheoryElement.TEXT,
			{
				config: accountNumber,
				theme: this.theme,
				fieldName: FIELD_NAMES.ACCOUNT_NUMBER,
				validation: /^[0-9]{3,17}$/
			},
			this.onChangeValidation
		);

		// Initialize other fields based on country
		switch (this.country) {
			case SUPPORTED_COUNTRIES.CA: {
				this.transitNumberField = await initBasisTheoryField(
					this.bt,
					BasisTheoryElement.TEXT,
					{
						config: transitNumber,
						theme: this.theme,
						validation: /^\d{5}$/,
						fieldName: FIELD_NAMES.TRANSIT_NUMBER
					},
					this.onChangeValidation
				);
				this.institutionNumberField = await initBasisTheoryField(
					this.bt,
					BasisTheoryElement.TEXT,
					{
						config: institutionNumber,
						theme: this.theme,
						fieldName: FIELD_NAMES.INSTITUTION_NUMBER,
						validation: /^\d{3}$/
					},
					this.onChangeValidation
				);
				break;
			}
			case SUPPORTED_COUNTRIES.US:
			default: {
				this.routingNumberField = await initBasisTheoryField(
					this.bt,
					BasisTheoryElement.TEXT,
					{
						config: routingNumber,
						theme: this.theme,
						validation: /^\d{9}$/,
						fieldName: FIELD_NAMES.ROUTING_NUMBER
					},
					this.onChangeValidation
				);
			}
		}
	};

	submitCallback = async () => {
		const submitButton = document.getElementById(this.configuration.submitButton.selector);
		submitButton.setAttribute("disabled", "true");

		/**
		 * @property {Boolean} success
		 * @property {Object} [paymentMethod]
		 * @property {CustomError[]} [errors]
		 */
		let apiResponse = { success: true, paymentMethod: null };

		const { accountNumber, routingNumber, transitNumber, institutionNumber } = this.configuration.fields;

		let btFields = {
			accountNumber: {
				field: this.accountNumberField,
				selector: accountNumber.selector
			}
		};
		switch (this.country) {
			case SUPPORTED_COUNTRIES.CA:
				btFields = {
					...btFields,
					institutionNumber: { field: this.institutionNumberField, selector: institutionNumber.selector },
					transitNumber: { field: this.transitNumberField, selector: transitNumber.selector }
				};
				break;
			default:
				btFields = {
					...btFields,
					routingNumber: { field: this.routingNumberField, selector: routingNumber.selector }
				};
		}

		const errors = await preValidateFields(
			this.billingAddressConfigs,
			this.billingInformation,
			this.theme,
			btFields,
			BasisTheoryTokenType.BANK
		);
		if (errors.length) {
			apiResponse = {
				success: false,
				errors
			};
			this.configuration.submitButton.callback(apiResponse);
			submitButton.removeAttribute("disabled");
			return;
		}

		const paymentFields = {
			account_number: this.accountNumberField
		};
		switch (this.country) {
			case SUPPORTED_COUNTRIES.CA:
				paymentFields.transit_number = this.transitNumberField;
				paymentFields.institution_number = this.institutionNumberField;
				break;
			case SUPPORTED_COUNTRIES.US:
			default:
				paymentFields.routing_number = this.routingNumberField;
				break;
		}
		const [tokenizationResult, btError] = await safeTry(
			tokenizeBankAccount(this.bt, paymentFields, this.billingInformation, this.country)
		);
		if (btError) {
			submitButton.removeAttribute("disabled");
			apiResponse = this.handleBtError(btError, BasisTheoryTokenType.BANK);
			this.configuration.submitButton.callback(apiResponse);
			return;
		}

		const [odinResponse, odinError] = await safeTry(this.api.putPaymentMethod(tokenizationResult));
		apiResponse = this.handleOdinResponse(odinResponse, odinError);
		submitButton.removeAttribute("disabled");
		this.configuration.submitButton.callback(apiResponse);
	};
}
